import {axiosClientAPI} from "@/lib/axios";
import {apiRoutes} from "@/lib/routes/api";

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export interface LoginResponse {
    access: string;
    refresh: string;
    user: {
        id: number;
        username: string;
        email: string;
        first_name: string;
        last_name: string;
        full_name: string;
        is_verified: boolean;
        role: string | null;
    };
}

export interface RegisterResponse {
    message: string;
    access: string;
    refresh: string;
    user: {
        id: number;
        username: string;
        email: string;
        first_name: string;
        last_name: string;
    };
}

export interface UserProfile {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number?: string | null;
    avatar?: {
        id: number;
        image: string;
        image_url: string;
        alt_text?: string | null;
    } | null;
    bio?: string | null;
    is_verified: boolean;
    is_active: boolean;
    is_staff: boolean;
    is_superuser?: boolean;
    date_joined: string;
    last_login?: string | null;
    created_at: string;
    updated_at: string;
    profile?: {
        date_of_birth?: string | null;
        gender?: string | null;
        job_title?: string | null;
        company?: string | null;
        department?: string | null;
        website?: string | null;
        linkedin?: string | null;
        twitter?: string | null;
        github?: string | null;
        timezone: string;
        language: string;
    } | null;
    role?: {
        id: number;
        name: string;
        code: string;
    } | null;
}

export const AuthService = {
    // Token management
    setTokens: (accessToken: string, refreshToken: string) => {
        if (typeof window !== 'undefined') {
            localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }
    },

    getAccessToken: () => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(ACCESS_TOKEN_KEY);
        }
        return null;
    },

    getRefreshToken: () => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(REFRESH_TOKEN_KEY);
        }
        return null;
    },

    removeTokens: () => {
        if (typeof window !== 'undefined') {
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem('token'); // Legacy token removal
        }
    },

    // Legacy token support (for backward compatibility)
    setToken: (token: string) => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('token', token);
            localStorage.setItem(ACCESS_TOKEN_KEY, token);
        }
    },

    getToken: () => {
        // Try new token first, fallback to legacy
        return AuthService.getAccessToken() || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
    },

    removeToken: () => {
        AuthService.removeTokens();
    },

    // Check if user is authenticated
    isAuthenticated: () => {
        return AuthService.getAccessToken() !== null;
    },

    // Login user
    login: async (username: string, password: string): Promise<LoginResponse> => {
        try {
            const response = await axiosClientAPI.post<LoginResponse>(apiRoutes.auth.login, {
                username,
                password
            });

            const data = response.data;

            // Store tokens
            if (data.access && data.refresh) {
                AuthService.setTokens(data.access, data.refresh);
            }

            return data;
        } catch (error: any) {
            console.error('Login error:', error);
            throw error;
        }
    },

    // Register user
    register: async (userData: {
        username: string;
        email: string;
        password: string;
        password_confirm: string;
        first_name?: string;
        last_name?: string;
        phone_number?: string;
    }): Promise<RegisterResponse> => {
        try {
            const response = await axiosClientAPI.post<RegisterResponse>(apiRoutes.auth.register, userData);

            const data = response.data;

            // Store tokens
            if (data.access && data.refresh) {
                AuthService.setTokens(data.access, data.refresh);
            }

            return data;
        } catch (error: any) {
            console.error('Register error:', error);
            throw error;
        }
    },

    // Refresh access token
    refreshToken: async (): Promise<string | null> => {
        try {
            const refreshToken = AuthService.getRefreshToken();
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await axiosClientAPI.post<{ access: string }>(
                apiRoutes.auth.tokenRefresh,
                { refresh: refreshToken }
            );

            const newAccessToken = response.data.access;
            if (newAccessToken) {
                localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
                return newAccessToken;
            }

            return null;
        } catch (error: any) {
            console.error('Token refresh error:', error);
            // If refresh fails, clear tokens and logout
            AuthService.removeTokens();
            throw error;
        }
    },

    // Logout user
    logout: async () => {
        try {
            const refreshToken = AuthService.getRefreshToken();
            
            // Call logout API to blacklist refresh token
            if (refreshToken) {
                try {
                    await axiosClientAPI.post(apiRoutes.auth.logout, {
                        refresh_token: refreshToken
                    });
                } catch (apiError) {
                    console.warn('Logout API call failed, continuing with client-side logout:', apiError);
                }
            }

            // Clear all client-side data
            AuthService.removeTokens();
            localStorage.removeItem('cyberrange_auth_user');
            localStorage.removeItem('session_expires');
            sessionStorage.clear();

            // Clear cookies
            document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';
            document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

            // Force redirect to login page
            if (typeof window !== 'undefined') {
                setTimeout(() => {
                    window.location.replace('/auth');
                }, 100);
            }
        } catch (error) {
            console.error('Logout error:', error);
            // Even if logout fails, clear local data
            AuthService.removeTokens();
            if (typeof window !== 'undefined') {
                window.location.replace('/auth');
            }
        }
    },

    // Get current user profile
    getProfile: async (): Promise<UserProfile> => {
        try {
            const token = AuthService.getAccessToken();
            
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axiosClientAPI.get<UserProfile>(apiRoutes.auth.me);
            return response.data;
        } catch (error: any) {
            console.error('Get profile error:', error);
            
            // If 401, try to refresh token
            if (error.response?.status === 401) {
                try {
                    await AuthService.refreshToken();
                    // Retry the request
                    const response = await axiosClientAPI.get<UserProfile>(apiRoutes.auth.me);
                    return response.data;
                } catch (refreshError) {
                    // Refresh failed, remove tokens
                    AuthService.removeTokens();
                    throw refreshError;
                }
            }
            
            throw error;
        }
    },

    // Change password
    changePassword: async (oldPassword: string, newPassword: string, newPasswordConfirm: string): Promise<void> => {
        try {
            await axiosClientAPI.post(apiRoutes.auth.changePassword, {
                old_password: oldPassword,
                new_password: newPassword,
                new_password_confirm: newPasswordConfirm
            });
        } catch (error: any) {
            console.error('Change password error:', error);
            throw error;
        }
    },

    // Request password reset
    requestPasswordReset: async (email: string): Promise<void> => {
        try {
            await axiosClientAPI.post(apiRoutes.auth.passwordReset, { email });
        } catch (error: any) {
            console.error('Password reset request error:', error);
            throw error;
        }
    },

    // Confirm password reset
    confirmPasswordReset: async (token: string, newPassword: string, newPasswordConfirm: string): Promise<void> => {
        try {
            await axiosClientAPI.post(apiRoutes.auth.passwordResetConfirm, {
                token,
                new_password: newPassword,
                new_password_confirm: newPasswordConfirm
            });
        } catch (error: any) {
            console.error('Password reset confirm error:', error);
            throw error;
        }
    },
};
